import * as React from 'react';
import { styled } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import MuiToolbar from '@mui/material/Toolbar';
import { tabsClasses } from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import SideMenuMobile from './SideMenuMobile';
import MenuButton from './MenuButton';
import ColorModeIconDropdown from '../theme/ColorModeIconDropdown';
import { useAuth } from '../auth/AuthContext';
import UserMenu from './UserMenu';
import { getPrimaryDisplayRole } from '../auth/authUtils';

const Toolbar = styled(MuiToolbar)({
  width: '100%',
  padding: '12px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  justifyContent: 'center',
  gap: '12px',
  flexShrink: 0,
  [`& ${tabsClasses.flexContainer}`]: {
    gap: '8px',
    p: '8px',
    pb: 0,
  },
});

export default function AppNavbar() {
  const [open, setOpen] = React.useState(false);
  const { user, realmRoles } = useAuth();
  const displayName = user?.name ?? user?.email ?? 'User';
  const displayRole = getPrimaryDisplayRole(realmRoles);

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        display: { xs: 'auto', md: 'none' },
        boxShadow: 0,
        bgcolor: 'background.paper',
        backgroundImage: 'none',
        borderBottom: '1px solid',
        borderColor: 'divider',
        top: 'var(--template-frame-height, 0px)',
      }}
    >
      <Toolbar variant="regular">
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            flexGrow: 1,
            width: '100%',
            gap: 1,
          }}
        >
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'center', mr: 'auto' }}>
            <CustomIcon />
            <Typography
              variant="h4"
              component="h1"
              sx={{ color: 'text.primary', display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <Box component="span">Empire</Box>
              <Box component="span" sx={{ color: '#3636F2' }}>
                FX
              </Box>
              {/* <Box component="span">CSP</Box> */}
            </Typography>
          </Stack>
          {user && (
            <Stack
              sx={{
                flex: '1 1 auto',
                minWidth: 0,
                alignItems: 'flex-end',
                textAlign: 'right',
                gap: 0.25,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.primary',
                  fontWeight: 600,
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
              >
                {displayName}
              </Typography>
              {user.email && user.name !== user.email && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    display: 'block',
                    lineHeight: 1.45,
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '100%',
                  }}
                >
                  {user.email}
                </Typography>
              )}
              {displayRole && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', pt: 0.25 }}>
                  <Chip
                    size="small"
                    label={displayRole}
                    variant="outlined"
                    color="primary"
                    sx={{
                      height: 20,
                      maxWidth: '100%',
                      '& .MuiChip-label': {
                        px: 0.65,
                        fontSize: '0.625rem',
                        fontWeight: 600,
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        textAlign: 'center',
                      },
                    }}
                  />
                </Box>
              )}
            </Stack>
          )}
          <UserMenu />
          <ColorModeIconDropdown />
          <MenuButton aria-label="menu" onClick={toggleDrawer(true)}>
            <MenuRoundedIcon />
          </MenuButton>
          <SideMenuMobile open={open} toggleDrawer={toggleDrawer} />
        </Stack>
      </Toolbar>
    </AppBar>
  );
}

export function CustomIcon() {
  return (
    <Box
      sx={{
        width: '1.5rem',
        height: '1.5rem',
        borderRadius: '999px',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        alignSelf: 'center',
      }}
    >
      <Box
        component="img"
        src="/favicon.ico"
        alt="Empire FX CSP"
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
    </Box>
  );
}
